import { apolloClientToProps, initializeApollo } from "../../../lib/apollo";
import getTranslations from "../../../lib/getTranslations";
import searchProductsQuery from "../../../src/Apollo/query/searchProducts.graphql";
import Blog from "../../../src/components/blog/Blog";
import { BlogPostEntity } from "@/common/contracts/strapi.contracts";
import { withLocale } from "@/common/utils/url.utils";
import { HeaderLanguageSwitcherProvider } from "@/components/Header/HeaderLanguageSwitcherContext";
import MainLayout from "@/components/Layouts/MainLayout";
import { tryGetBlogPost, tryGetRelatedBlogPosts } from "@/components/blog/api";
import { defaultLocale, locales, toContentLocale } from "@/i18n/config";

type BlogPageProps = {
  blogPost: BlogPostEntity | null;
  relatedBlogPosts: BlogPostEntity[] | null;
  languageSwitcherUrls?: Record<string, string>;
  hreflangUrls?: Record<string, string>;
};

const BlogPage = ({
  blogPost,
  relatedBlogPosts,
  hreflangUrls,
}: BlogPageProps) => {
  return (
    <Blog
      blogPost={blogPost}
      relatedBlogPosts={relatedBlogPosts}
      hreflangUrls={hreflangUrls}
    />
  );
};

BlogPage.getLayout = (page: JSX.Element, props: any) => {
  return (
    <HeaderLanguageSwitcherProvider
      value={{ urls: props.languageSwitcherUrls }}
    >
      <MainLayout>{page}</MainLayout>
    </HeaderLanguageSwitcherProvider>
  );
};

const blogUrlWithPrefix = (slug: string, locale: string) =>
  withLocale(`/blog/${slug}`, locale, defaultLocale);

// Unprefixed `/blog/<slug>` per locale. <HrefLang /> adds the locale prefix
// itself, so it must get these; the header switcher wants them prefixed.
const createBlogPaths = (
  responseBlogPost: Awaited<ReturnType<typeof tryGetBlogPost>>
) => {
  const attributes = responseBlogPost.blogPost?.attributes;
  if (!attributes) {
    return {};
  }
  const localizations = attributes.localizations?.data ?? [];
  const paths = localizations.reduce<Record<string, string>>(
    (acc, localization) => {
      const { locale, slug } = localization.attributes ?? {};
      if (locale && slug) {
        acc[locale] = `/blog/${slug}`;
      }
      return acc;
    },
    {}
  );
  if (attributes.locale && attributes.slug) {
    paths[attributes.locale] = `/blog/${attributes.slug}`;
  }

  // `en-us` reads the `en` post (WHO-5766): same path, its own prefix.
  for (const routedLocale of locales) {
    const contentPath = paths[toContentLocale(routedLocale)];

    if (!paths[routedLocale] && contentPath) {
      paths[routedLocale] = contentPath;
    }
  }

  return paths;
};

const withLocalePrefix = (paths: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(paths).map(([locale, path]) => [
      locale,
      withLocale(path, locale, defaultLocale),
    ])
  );

export const getServerSideProps = async ({
  locale,
  params,
}: {
  locale: string;
  params: { slug: string };
}) => {
  const { slug } = params as { slug: string };

  if (typeof slug !== "string") {
    return { notFound: true };
  }

  const contentLocale = toContentLocale(locale);
  const [responseBlogPost, responseRelatedBlogPosts] = await Promise.all([
    tryGetBlogPost({ locale: contentLocale, slug }),
    tryGetRelatedBlogPosts({ locale: contentLocale, slug }),
  ]);

  if (!responseBlogPost.blogPost?.attributes) {
    return { notFound: true };
  }

  if (responseBlogPost.blogPost.attributes.slug !== slug) {
    const blogPostSlug = responseBlogPost.blogPost.attributes.slug ?? "";
    return {
      redirect: {
        destination: blogUrlWithPrefix(blogPostSlug, locale),
        permanent: true,
      },
    };
  }

  const client = initializeApollo();

  await client.query({
    query: searchProductsQuery,
    variables: {
      input: {
        query: "",
        ...responseBlogPost?.blogPost?.attributes?.search,
        pagination: {
          limit: 4,
        },
      },
      lang: contentLocale.toUpperCase(),
    },
  });

  const blogPaths = createBlogPaths(responseBlogPost);

  return {
    props: {
      ...(await getTranslations(locale, ["blog"])),
      ...apolloClientToProps(client).props,
      blogPost: responseBlogPost.blogPost,
      relatedBlogPosts: responseRelatedBlogPosts.blogPosts,
      languageSwitcherUrls: withLocalePrefix(blogPaths),
      hreflangUrls: blogPaths,
    },
  };
};

export default BlogPage;
